/**
 * CryptoService — stateless libsodium wrapper for master password and note key operations.
 *
 * Security rules:
 * - Key material (masterKey, noteKey) lives in memory only — never serialised or logged.
 * - Call `zeroize()` on any Uint8Array holding key material before discarding it.
 * - The server never receives the password or any derived key in plaintext; only
 *   KDF parameters, verifiers, and ciphertexts are persisted.
 */

import sodium from 'libsodium-wrappers-sumo'

// Fixed context string mixed into the verifier so it cannot be reused for other
// purposes even if the masterKey leaks.
const VERIFIER_CONTEXT = 'memoslate:master-password-verifier:v1'
const BODY_PREFIX = 'memoslate-body:v1:'

export type DeriveKeyResult = {
    masterKey: Uint8Array
    salt: Uint8Array
    opslimit: number
    memlimit: number
}

export type MasterPasswordMetadata = {
    /** Base64-encoded 16-byte random salt */
    salt: string
    opslimit: number
    memlimit: number
    alg: 'argon2id13'
    /** Base64-encoded BLAKE2b verifier */
    password_verifier: string
}

export type NoteKeyWrapperPayload = {
    /** Base64-encoded secretbox ciphertext of the note key */
    wrapped_key: string
    /** Base64-encoded nonce used when wrapping */
    nonce: string
    alg: 'secretbox'
}

let _ready = false

/**
 * Initialise libsodium. Must be awaited before any other function is called.
 * Safe to call multiple times — subsequent calls are no-ops.
 */
export async function initSodium(): Promise<void> {
    if (_ready) return
    await sodium.ready
    _ready = true
}

function assertReady(): void {
    if (!_ready) {
        throw new Error('CryptoService: call initSodium() before use.')
    }
}

/**
 * Derive a 32-byte master key from a user-supplied password using Argon2id.
 *
 * @param password  - Plaintext password (will NOT be stored or logged).
 * @param salt      - Optional existing salt (Uint8Array). When omitted a fresh
 *                    random salt is generated (use this path at setup time).
 * @param opslimit  - Argon2id ops limit. Defaults to SENSITIVE.
 * @param memlimit  - Argon2id memory limit (bytes). Defaults to SENSITIVE.
 * @returns         - { masterKey, salt, opslimit, memlimit }
 *
 * ⚠️  The returned `masterKey` must be zeroized with `zeroize()` when no
 *     longer needed.
 */
export function deriveKey(
    password: string,
    salt?: Uint8Array,
    opslimit?: number,
    memlimit?: number,
): DeriveKeyResult {
    assertReady()

    const resolvedSalt = salt ?? sodium.randombytes_buf(sodium.crypto_pwhash_SALTBYTES)
    const resolvedOpslimit = opslimit ?? sodium.crypto_pwhash_OPSLIMIT_SENSITIVE
    const resolvedMemlimit = memlimit ?? sodium.crypto_pwhash_MEMLIMIT_SENSITIVE

    const masterKey = sodium.crypto_pwhash(
        32,
        password,
        resolvedSalt,
        resolvedOpslimit,
        resolvedMemlimit,
        sodium.crypto_pwhash_ALG_ARGON2ID13,
    )

    return {
        masterKey,
        salt: resolvedSalt,
        opslimit: resolvedOpslimit,
        memlimit: resolvedMemlimit,
    }
}

/**
 * Compute a BLAKE2b-based password verifier from the master key.
 *
 * Formula: BLAKE2b(masterKey || VERIFIER_CONTEXT)
 *
 * The verifier is stored server-side and used to validate the password on
 * unlock without exposing the master key itself.
 *
 * @param masterKey - 32-byte master key produced by `deriveKey()`.
 * @returns         - Raw 32-byte verifier (Uint8Array).
 */
export function computeVerifier(masterKey: Uint8Array): Uint8Array {
    assertReady()

    const contextBytes = sodium.from_string(VERIFIER_CONTEXT)
    const message = new Uint8Array(masterKey.length + contextBytes.length)
    message.set(masterKey, 0)
    message.set(contextBytes, masterKey.length)

    const verifier = sodium.crypto_generichash(32, message, null)

    // Zeroize the intermediate concatenated buffer.
    sodium.memzero(message)

    return verifier
}

/**
 * Verify a password by rederiving the master key and doing a constant-time
 * comparison of the stored verifier against a freshly computed one.
 *
 * @param masterKey       - Freshly derived master key to test.
 * @param storedVerifier  - Raw verifier bytes previously produced by
 *                          `computeVerifier()` and stored server-side.
 * @returns               - `true` if the password matches, `false` otherwise.
 */
export function verifyPassword(
    masterKey: Uint8Array,
    storedVerifier: Uint8Array,
): boolean {
    assertReady()

    const candidate = computeVerifier(masterKey)

    if (candidate.length !== storedVerifier.length) {
        sodium.memzero(candidate)
        return false
    }

    // Constant-time compare — sodium.memcmp returns true when buffers are equal.
    const match = sodium.memcmp(candidate, storedVerifier)
    sodium.memzero(candidate)
    return match
}

/**
 * Securely zero out a Uint8Array holding sensitive key material.
 * Always call this before setting a key reference to null.
 *
 * @param buffer - The buffer to wipe.
 */
export function zeroize(buffer: Uint8Array): void {
    assertReady()
    sodium.memzero(buffer)
}

/**
 * Convenience helper: build the `MasterPasswordMetadata` payload that is
 * sent to the server after a successful master-password setup.
 *
 * This function does NOT perform any network calls — it only serialises the
 * KDF parameters and verifier into the expected server contract shape.
 *
 * @param deriveResult  - Object returned by `deriveKey()`.
 * @param verifier      - Raw verifier bytes returned by `computeVerifier()`.
 * @returns             - Serialisable `MasterPasswordMetadata` object.
 */
export function buildMetadataPayload(
    deriveResult: DeriveKeyResult,
    verifier: Uint8Array,
): MasterPasswordMetadata {
    return {
        salt: sodium.to_base64(deriveResult.salt),
        opslimit: deriveResult.opslimit,
        memlimit: deriveResult.memlimit,
        alg: 'argon2id13',
        password_verifier: sodium.to_base64(verifier),
    }
}

/**
 * Decode a base64-encoded salt string (as stored in `MasterPasswordMetadata`)
 * back to a `Uint8Array` for use with `deriveKey()` on unlock.
 */
export function decodeSalt(base64Salt: string): Uint8Array {
    assertReady()
    return sodium.from_base64(base64Salt)
}

/**
 * Decode a base64-encoded verifier string back to a `Uint8Array` for use
 * with `verifyPassword()` on unlock.
 */
export function decodeVerifier(base64Verifier: string): Uint8Array {
    assertReady()
    return sodium.from_base64(base64Verifier)
}

/**
 * Generate a fresh random 32-byte note key using libsodium's keygen helper.
 *
 * ⚠️  The returned key must be zeroized with `zeroize()` by the caller when
 *     it is no longer needed. This function does NOT zeroize it.
 *
 * @returns - Raw 32-byte symmetric note key (Uint8Array).
 */
export function generateNoteKey(): Uint8Array {
    assertReady()
    return sodium.crypto_secretbox_keygen()
}

/** Encrypt a complete note body with the note key. Purpose and note identity are
 * authenticated inside the secretbox payload so envelopes cannot be swapped. */
export function encryptNoteBody(body: string, noteId: string, noteKey: Uint8Array): string {
    assertReady()
    const nonce = sodium.randombytes_buf(sodium.crypto_secretbox_NONCEBYTES)
    const plaintext = sodium.from_string(JSON.stringify({ purpose: 'memoslate:note-body:v1', note_id: noteId, body }))
    try {
        const ciphertext = sodium.crypto_secretbox_easy(plaintext, nonce, noteKey)
        return `${BODY_PREFIX}${sodium.to_base64(nonce, sodium.base64_variants.ORIGINAL)}:${sodium.to_base64(ciphertext, sodium.base64_variants.ORIGINAL)}`
    } finally {
        sodium.memzero(nonce)
        sodium.memzero(plaintext)
    }
}

export function decryptNoteBody(envelope: string, noteId: string, noteKey: Uint8Array): string {
    assertReady()
    const parts = envelope.split(':')
    if (parts.length !== 4 || `${parts[0]}:${parts[1]}:` !== BODY_PREFIX) {
        throw new Error('Unsupported encrypted note body format.')
    }
    const nonce = sodium.from_base64(parts[2], sodium.base64_variants.ORIGINAL)
    const ciphertext = sodium.from_base64(parts[3], sodium.base64_variants.ORIGINAL)
    let plaintext: Uint8Array | null = null
    try {
        plaintext = sodium.crypto_secretbox_open_easy(ciphertext, nonce, noteKey)
        const payload = JSON.parse(sodium.to_string(plaintext)) as { purpose?: string; note_id?: string; body?: unknown }
        if (payload.purpose !== 'memoslate:note-body:v1' || payload.note_id !== noteId || typeof payload.body !== 'string') {
            throw new Error('Encrypted note body authentication failed.')
        }
        return payload.body
    } finally {
        sodium.memzero(nonce)
        sodium.memzero(ciphertext)
        if (plaintext) sodium.memzero(plaintext)
    }
}

/**
 * Wrap (encrypt) a note key with the user's master key using
 * `crypto_secretbox_easy` (XSalsa20-Poly1305).
 *
 * A fresh random nonce is generated on every call, so encrypting the same
 * noteKey twice always produces different ciphertext.
 *
 * ⚠️  The caller owns `noteKey`'s lifetime — this function does NOT zeroize it.
 *     Only the internally-allocated nonce buffer is managed here.
 *
 * @param noteKey   - 32-byte note key to wrap. Caller retains ownership.
 * @param masterKey - 32-byte master key used as the wrapping key.
 * @returns         - `NoteKeyWrapperPayload` ready to be sent to the server.
 */
export function wrapNoteKey(noteKey: Uint8Array, masterKey: Uint8Array): NoteKeyWrapperPayload {
    assertReady()

    const nonce = sodium.randombytes_buf(sodium.crypto_secretbox_NONCEBYTES)
    const ciphertext = sodium.crypto_secretbox_easy(noteKey, nonce, masterKey)

    const payload: NoteKeyWrapperPayload = {
        wrapped_key: sodium.to_base64(ciphertext),
        nonce:       sodium.to_base64(nonce),
        alg:         'secretbox',
    }

    // Zeroize intermediate buffers allocated in this function.
    sodium.memzero(nonce)

    return payload
}

/**
 * Unwrap (decrypt) a note key from its stored wrapper using the user's
 * master key via `crypto_secretbox_open_easy`.
 *
 * @param wrappedKey - Base64-encoded secretbox ciphertext of the note key.
 * @param nonce      - Base64-encoded nonce used when the key was wrapped.
 * @param masterKey  - 32-byte master key used as the unwrapping key.
 * @returns          - Raw 32-byte note key (Uint8Array). Caller takes
 *                     ownership and must zeroize it when no longer needed.
 * @throws           - If decryption fails (wrong master key or tampered data).
 */
export function unwrapNoteKey(
    wrappedKey: string,
    nonce: string,
    masterKey: Uint8Array,
): Uint8Array {
    assertReady()

    const wrappedKeyBytes = sodium.from_base64(wrappedKey)
    const nonceBytes = sodium.from_base64(nonce)

    const noteKey = sodium.crypto_secretbox_open_easy(wrappedKeyBytes, nonceBytes, masterKey)

    // Zeroize intermediate buffers.
    sodium.memzero(nonceBytes)

    return noteKey
}

// ---------------------------------------------------------------------------
// Field-level encrypt / decrypt
// ---------------------------------------------------------------------------

/** Prefix that marks a field value as encrypted. */
const ENCRYPTED_PREFIX = 'enc:'

/**
 * Check whether a secret-field content string is already encrypted.
 *
 * @param content - The raw content string from the SecretFieldNode.
 * @returns `true` when the value starts with the `enc:` prefix.
 */
export function isEncryptedFieldValue(content: string): boolean {
    return content.startsWith(ENCRYPTED_PREFIX)
}

/**
 * Encrypt a plaintext secret-field value with the note key using
 * `crypto_secretbox_easy` (XSalsa20-Poly1305).
 *
 * A fresh random nonce is generated per call, so encrypting the same
 * plaintext twice always produces different ciphertext.
 *
 * Output format: `enc:<base64(nonce)>:<base64(ciphertext)>`
 *
 * @param plaintext - The plaintext string to encrypt.
 * @param noteKey   - 32-byte note key.
 * @returns         - Formatted encrypted string ready to be stored in the document.
 */
export function encryptFieldValue(plaintext: string, noteKey: Uint8Array): string {
    assertReady()

    const plaintextBytes = sodium.from_string(plaintext)
    const nonce = sodium.randombytes_buf(sodium.crypto_secretbox_NONCEBYTES)
    const ciphertext = sodium.crypto_secretbox_easy(plaintextBytes, nonce, noteKey)

    const encoded = `${ENCRYPTED_PREFIX}${sodium.to_base64(nonce)}:${sodium.to_base64(ciphertext)}`

    // Zeroize intermediate plaintext bytes.
    sodium.memzero(plaintextBytes)
    sodium.memzero(nonce)

    return encoded
}

/**
 * Decrypt an encrypted secret-field value with the note key.
 *
 * Expected input format: `enc:<base64(nonce)>:<base64(ciphertext)>`
 *
 * @param encoded - The encrypted string produced by `encryptFieldValue()`.
 * @param noteKey - 32-byte note key.
 * @returns       - The decrypted plaintext string.
 * @throws        - If the format is invalid or decryption fails (wrong key / tampered data).
 */
export function decryptFieldValue(encoded: string, noteKey: Uint8Array): string {
    assertReady()

    if (!isEncryptedFieldValue(encoded)) {
        throw new Error('CryptoService: value is not in encrypted format.')
    }

    // Strip the prefix and split into nonce and ciphertext parts.
    const payload = encoded.slice(ENCRYPTED_PREFIX.length)
    const separatorIndex = payload.indexOf(':')
    if (separatorIndex === -1) {
        throw new Error('CryptoService: malformed encrypted value — missing separator.')
    }

    const nonceB64 = payload.slice(0, separatorIndex)
    const ciphertextB64 = payload.slice(separatorIndex + 1)

    if (!nonceB64 || !ciphertextB64) {
        throw new Error('CryptoService: malformed encrypted value — empty nonce or ciphertext.')
    }

    const nonce = sodium.from_base64(nonceB64)
    const ciphertext = sodium.from_base64(ciphertextB64)

    const plaintextBytes = sodium.crypto_secretbox_open_easy(ciphertext, nonce, noteKey)
    const plaintext = sodium.to_string(plaintextBytes)

    // Zeroize intermediate buffers.
    sodium.memzero(plaintextBytes)
    sodium.memzero(nonce)

    return plaintext
}

const CryptoService = {
    initSodium,
    deriveKey,
    computeVerifier,
    verifyPassword,
    zeroize,
    buildMetadataPayload,
    decodeSalt,
    decodeVerifier,
    generateNoteKey,
    wrapNoteKey,
    unwrapNoteKey,
    encryptFieldValue,
    decryptFieldValue,
    isEncryptedFieldValue,
}

export default CryptoService
